import type { IconName } from '../components/Icon';
import type { CompanyContext } from './context';
import type { Phrase } from './i18n';
import { NAV, visibleNav, type NavItem } from './nav';

/**
 * 🔴 THE SIDEBAR'S DEPARTMENTS — the back-office redesign (owner, 2026-09-25).
 *
 * The owner saw forty-seven sections in one row of tabs and asked for it to be fixed. They are now
 * grouped by the DEPARTMENT that works them, in a sidebar.
 *
 * ⚠️ `NAV`'S ORDER IS KEPT INSIDE EVERY GROUP. `nav.ts` records why each entry sits where it does —
 * *"BEFORE Bills, because that is the order the purchase happens in"* — and those arguments are about
 * neighbours WITHIN a department. So a group lists which entries it holds, and their order is always
 * NAV's: a group can never reorder what `nav.ts` argued for. `nav-groups.spec.tsx` holds every NAV entry
 * in exactly ONE group, so a new screen cannot be added to the product and fall out of the sidebar.
 *
 * The module gating is untouched: a group shows what `visibleNav` shows, and a group left with
 * nothing is not drawn at all.
 */

export type NavGroupKey =
  | 'home'
  | 'sales'
  | 'purchasing'
  | 'inventory'
  | 'money'
  | 'accounting'
  | 'projects'
  | 'people'
  | 'insight';

export interface NavGroup {
  key: NavGroupKey;
  /** Chrome: a phrase the Bangla dictionary has (DS-4), so a new department cannot ship without its Bangla. */
  label: Phrase;
  /** Its icon in the kit's set (`components/Icon.tsx`, Lucide's geometry; DS-11b3). */
  icon: IconName;
  /** Which NAV entries it holds. Their ORDER is NAV's, never this list's. */
  holds: readonly string[];
}

/*
 * DS-11b3 (F477, F581): the departments' icons are the kit's (`components/Icon.tsx`, Lucide's geometry on Lucide's
 * grid), named as `lucide-react` names them. The thirteen hand-drawn paths that were here (`NAV_ICONS`) are gone.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  { key: 'home', label: 'Home', icon: 'house', holds: ['/', '/approvals'] },
  {
    key: 'sales',
    label: 'Sales & customers',
    icon: 'fileText',
    holds: ['/invoices', '/credit-notes', '/partners', '/leads', '/activities', '/pipeline', '/subscriptions', '/marketplace', '/couriers', '/store-payments', '/export-lcs'],
  },
  {
    key: 'purchasing',
    label: 'Purchasing',
    icon: 'shoppingCart',
    holds: ['/blanket-orders', '/purchase-orders', '/goods-receipts', '/letters-of-credit', '/landed-costs', '/bills', '/expenses', '/recurring-costs'],
  },
  {
    key: 'inventory',
    label: 'Inventory',
    icon: 'package',
    holds: ['/items', '/stock-transfers', '/stock-counts', '/stock-adjustments', '/boms', '/pharma'],
  },
  { key: 'money', label: 'Money & bank', icon: 'creditCard', holds: ['/payments', '/post-dated-cheques', '/bank'] },
  {
    key: 'accounting',
    label: 'Accounting',
    icon: 'bookText',
    holds: ['/journal-drafts', '/journals', '/vat-adjustments', '/turnover-tax-adjustments', '/fixed-assets', '/depreciation', '/funds', '/periods', '/audit-log', '/operator-activity'],
  },
  { key: 'projects', label: 'Projects', icon: 'briefcase', holds: ['/projects', '/construction'] },
  {
    key: 'people',
    label: 'HR & payroll',
    icon: 'users',
    holds: ['/payroll', '/organisation', '/shifts', '/attendance', '/holidays', '/wage-boards', '/loans-fines', '/my-payslips'],
  },
  // F65: a report page, not a bare bar chart; Settings itself is in the user menu since DS-11a3.
  { key: 'insight', label: 'Reports & settings', icon: 'fileChartColumn', holds: ['/reports', '/settings'] },
];

export interface VisibleGroup extends Omit<NavGroup, 'holds'> {
  items: readonly NavItem[];
}

/** The sidebar this company sees: each group's visible items in NAV's order, and no empty group. */
export function groupedNav(context: CompanyContext | null | undefined): readonly VisibleGroup[] {
  const visible = visibleNav(context);
  return NAV_GROUPS.map(({ holds, ...group }) => ({
    ...group,
    items: visible.filter((item) => holds.includes(item.to)),
  })).filter((group) => group.items.length > 0);
}

/** The group a path belongs to — the longest NAV entry it sits under, so `/invoices/INV-1` opens Sales. */
export function groupOf(pathname: string): NavGroupKey | null {
  const entry = [...NAV]
    .filter((item) => (item.to === '/' ? pathname === '/' : pathname === item.to || pathname.startsWith(`${item.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  if (!entry) return null;
  return NAV_GROUPS.find((g) => g.holds.includes(entry.to))?.key ?? null;
}
